<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title>{{ $t('dashboard.employeeStatistics') }}</b-card-title>
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        size="sm"
        :disabled="isLoading"
        @click="refreshData"
      >
        <feather-icon
          icon="RefreshCwIcon"
          :class="{ 'animate-spin': isLoading }"
        />
      </b-button>
    </b-card-header>
    <b-card-body v-if="employees">
      <!-- Total Employees -->
      <div class="text-center mb-2">
        <h2 class="font-weight-bolder mb-1">
          {{ employees.total_employees }}
        </h2>
        <span class="text-muted">{{ $t('dashboard.totalEmployees') }}</span>
      </div>

      <!-- Status Breakdown -->
      <h6 class="mb-1">
        {{ $t('dashboard.byStatus') }}
      </h6>
      <b-list-group class="mb-2">
        <b-list-group-item
          v-for="(count, status) in employees.by_status"
          :key="status"
          class="d-flex justify-content-between align-items-center"
        >
          <b-badge :variant="getStatusVariant(status)">
            {{ $t(`employee.status.${status}`) }}
          </b-badge>
          <strong>{{ count }}</strong>
        </b-list-group-item>
      </b-list-group>

      <!-- Department Distribution -->
      <h6 class="mb-1">
        {{ $t('dashboard.byDepartment') }}
      </h6>
      <vue-apex-charts
        type="bar"
        height="250"
        :options="departmentChartOptions"
        :series="departmentChartSeries"
      />

      <!-- Additional Stats -->
      <b-row class="mt-2">
        <b-col cols="6">
          <b-card
            bg-variant="success"
            text-variant="white"
            class="text-center mb-0"
          >
            <h4 class="text-white mb-0">
              {{ employees.recently_hired }}
            </h4>
            <small>{{ $t('dashboard.recentlyHired') }}</small>
          </b-card>
        </b-col>
        <b-col cols="6">
          <b-card
            bg-variant="warning"
            text-variant="white"
            class="text-center mb-0"
          >
            <h4 class="text-white mb-0">
              {{ employees.in_training }}
            </h4>
            <small>{{ $t('dashboard.inTraining') }}</small>
          </b-card>
        </b-col>
      </b-row>
    </b-card-body>

    <!-- Loading State -->
    <b-card-body v-else-if="isLoading">
      <div class="text-center py-3">
        <b-spinner variant="primary" />
      </div>
    </b-card-body>

    <!-- Empty State -->
    <b-card-body v-else>
      <div class="text-center py-3">
        <p class="text-muted">
          {{ $t('dashboard.noDataAvailable') }}
        </p>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BBadge,
  BSpinner,
  BListGroup,
  BListGroupItem,
} from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'EmployeeStatisticsWidget',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BBadge,
    BSpinner,
    BListGroup,
    BListGroupItem,
    VueApexCharts,
  },
  directives: {
    Ripple,
  },
  computed: {
    ...mapGetters('dashboard', ['employees', 'loading']),
    isLoading() {
      return this.loading.employees
    },
    departmentChartSeries() {
      if (!this.employees || !this.employees.by_department) return []
      return [
        {
          name: this.$t('dashboard.employees'),
          data: this.employees.by_department.map(dept => dept.count),
        },
      ]
    },
    departmentChartOptions() {
      if (!this.employees || !this.employees.by_department) return {}
      return {
        chart: {
          type: 'bar',
        },
        xaxis: {
          categories: this.employees.by_department.map(dept => dept.department),
        },
        colors: ['#3b82f6'],
        plotOptions: {
          bar: {
            borderRadius: 8,
            horizontal: true,
          },
        },
      }
    },
  },
  methods: {
    ...mapActions('dashboard', ['fetchEmployees', 'refreshWidget']),
    refreshData() {
      this.refreshWidget({ widget: 'employees' })
    },
    getStatusVariant(status) {
      const variants = {
        training: 'warning',
        contracted: 'success',
        terminated: 'danger',
      }
      return variants[status] || 'secondary'
    },
  },
}
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
